import React from 'react';
import {observer, inject} from 'mobx-react';
import {Provider} from 'mobx-react';
import {reaction} from 'mobx';
import {ScreenLoader} from '../../components/screen-loader/screen-loader';
import {InviteMobileController} from './InviteMobileController';
import * as patterns from '@techsee/techsee-common/lib/constants/utils.patterns.js';
import get from 'lodash/get';
import {requireImage} from '../../utils/utils';
import {resolveLogoUrl} from '../../utils/logo-url.utils';
import {RolesConstant} from '@techsee/techsee-common/lib/constants/account.constants';
import {TsMultiSelect} from '../../components/ts-multi-select/ts-multi-select.component';
import {TsCountrySelect} from '../../components/ts-country-select/ts-country-select';
import {SwitchButton} from '@techsee/techsee-ui-common/lib/forms/buttons/switch';
import {ChangeSettingsModal} from '../../modals/change-settings-modal/ChangeSettingsModal.component';
import {ChangeAccountModal} from '../../modals/change-account-modal/ChangeAccountModal.component';
import {SimpleModal} from '../../components/simple-modal/component';
import {AcceptTermsModal} from '../../modals/accept-terms-modal/AcceptTermsModal.component';
import {SmsStatusModal} from '../../modals/sms-status-modal/SmsStatusModal.component';
import {LookupNumber} from '../../states/invite-new/invite-wizard/components/lookup-number/component';
import {TechseeBaseComponent} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-component';
import '../../states/invite-new/invite-wizard/components/lookup-number/styles.scss';
import './invite-mobile.style.scss';

export interface InviteMobileRootProps {
    stores?: Record<string, unknown>;
}

const getInviteIcon = (type: 'sms' | 'email' | 'whatsapp', isSelected: boolean): string => {
    const selectedSuffix = isSelected ? '-selected' : '';
    return requireImage(`icon-new-${type}${selectedSuffix}.png`);
};

interface InviteMobileViewProps {
    inviteMobileController?: InviteMobileController;
}

interface InviteMobileViewComponentProps {
    inviteMobileController: InviteMobileController;
}

@observer
class InviteMobileViewComponent extends TechseeBaseComponent<InviteMobileViewComponentProps> {
    private notificationsRef = React.createRef<HTMLDivElement>();
    private scrollReactionDisposer?: () => void;

    componentDidMount(): void {
        const {state} = this.props.inviteMobileController;
        // React to error/message changes using MobX reaction
        this.scrollReactionDisposer = reaction(
            () => ({
                error: state.customError?.message,
                status: state.statusMessage,
                message: state.message
            }),
            () => {
                // Scroll when error or message appears
                if (state.customError || state.statusMessage || state.message) {
                    this.scrollToNotifications();
                }
            },
            {fireImmediately: false}
        );
    }

    componentWillUnmount(): void {
        if (this.scrollReactionDisposer) {
            this.scrollReactionDisposer();
        }
    }

    private scrollToNotifications = (): void => {
        if (this.notificationsRef.current) {
            requestAnimationFrame(() => {
                const element = this.notificationsRef.current;
                if (element) {
                    element.scrollIntoView({behavior: 'smooth', block: 'start', inline: 'nearest'});
                }
            });
        }
    };

    renderInternal(): JSX.Element {
        const inviteMobileController = this.props.inviteMobileController;
        const {state, translate, useNewInterface, fullName, isLive3, isRTL, isIOS} = inviteMobileController;
        const mobilePhonePattern = patterns.mobilePhone as RegExp | string | undefined;
        let phonePattern = '';
        const escapeForHtmlPattern = (patternStr: string): string => {
            // Process each character class [...]; Safari/mobile uses v flag which forbids unescaped ( ) in char classes
            return patternStr.replace(/\[([^\]]*)\]/g, (match, inner) => {
                let escaped = inner;
                // Escape dashes in middle of char class - but NOT in simple ranges like [0-9] or [a-z]
                const isSimpleRange = /^[0-9a-zA-Z]-[0-9a-zA-Z]$/.test(inner);
                if (!isSimpleRange) {
                    escaped = escaped.replace(/(?<=.)(?<!\\)-(?=.)/g, '\\-');
                }
                // Escape ( and ) in char classes for Safari v-flag compatibility
                escaped = escaped.replace(/(^|[^\\])\(/g, '$1\\(').replace(/(^|[^\\])\)/g, '$1\\)');
                return `[${escaped}]`;
            });
        };
        if (mobilePhonePattern instanceof RegExp) {
            phonePattern = escapeForHtmlPattern(mobilePhonePattern.source);
        } else if (typeof mobilePhonePattern === 'string') {
            phonePattern = escapeForHtmlPattern(mobilePhonePattern.replace(/^\/|\/[gimuy]*$/g, ''));
        }
        const accountData = state.accountData as Record<string, any> | undefined;
        const currentUser = state.currentUser as Record<string, any> | undefined;
        const {
            fsSettings,
            customerId: customerIdSettings,
            allowInviteBySms = true,
            allowSendByEmail = true,
            allowSessionWithoutSms,
            accountOfflineSessions,
            multiLanguage
        } = accountData?.settings || {};
        const {
            smsSettings,
            enableClientSessionInitiation: csiEnabled,
            allowSearchOfflineSessionsById,
            liveSdk,
            coBrowsing,
            audioStartMode,
            v3
        } = accountData?.protectedSettings || {};
        const {fsInviteBySMS, fsInviteByEmail, fsInviteByWhatsApp} = fsSettings || {};
        const addCountryCodeToSms = Boolean(smsSettings?.addCountryCodeToSms);
        const isTechnicianSupervisor = currentUser?.role === RolesConstant.TECHNICIAN_SUPERVISOR;
        const canObserve = Boolean(currentUser?.canObserve);
        const allowedToJoinByCodeOrGuid = csiEnabled || (accountOfflineSessions && allowSearchOfflineSessionsById);
        const showSuperviseSession = canObserve && !isTechnicianSupervisor;
        const showOrSeparator = allowedToJoinByCodeOrGuid || showSuperviseSession;
        const showLanguageSelection = Boolean(multiLanguage);
        const showAudioToggle = Boolean(audioStartMode);
        const containerClasses = [
            'ts-container',
            'ts-invite',
            'ts-mobile-invite',
            isIOS ? 'ts-mobile-ios' : '',
            useNewInterface ? 'bright-invite' : '',
            isTechnicianSupervisor ? 'ts-technicianSupervisor' : ''
        ]
            .filter(Boolean)
            .join(' ');

        return (
            <div className={containerClasses}>
                <div id='background-runner' />
                <div className='ts-header-wrapper'>
                    <header className='ts-topbar'>
                        <div className='logo'>
                            <img src={resolveLogoUrl(get(accountData, 'settings.logoUrl', ''))} alt='logo' />
                        </div>
                        <div className='ts-topbar-content'>
                            <div />
                            {!isLive3 && (
                                <div
                                    className='ts-hamburger-container'
                                    onClick={() => inviteMobileController.toggleOverlay()}>
                                    <div />
                                    <div />
                                    <div />
                                </div>
                            )}
                            <span className='ts-user-badge pull-right' dir='ltr'>
                                <small className='name-area'>{fullName}</small>
                                <i className='glyphicon glyphicon-cog' />
                            </span>
                        </div>
                    </header>
                </div>
                {!isLive3 && (
                    <div
                        className={`ts-right-overlay ${state.isOverlayOpen ? 'ts-overlay-open' : ''}`}
                        onClick={(e) => {
                            if (e.target === e.currentTarget) {
                                inviteMobileController.toggleOverlay();
                            }
                        }}>
                        <div className='ts-overlay-menu'>
                            <div className='ts-user-name'>{fullName}</div>
                            <ul>
                                <li>
                                    <a
                                        href='#'
                                        onClick={(e) => {
                                            e.preventDefault();
                                            inviteMobileController.toggleOverlay();
                                            inviteMobileController.changeUserSettings();
                                        }}>
                                        {translate('INVITE.VIEW.SETTINGS')}
                                    </a>
                                </li>
                                {Boolean(currentUser?.parentSubUserId) && (
                                    <li>
                                        <a
                                            href='#'
                                            onClick={(e) => {
                                                e.preventDefault();
                                                inviteMobileController.toggleOverlay();
                                                inviteMobileController.switchAccount();
                                            }}>
                                            {translate('LOGIN.VIEW.SWITCH_ACCOUNT')}
                                        </a>
                                    </li>
                                )}
                                <li>
                                    <a
                                        href='#'
                                        onClick={(e) => {
                                            e.preventDefault();
                                            inviteMobileController.toggleOverlay();
                                            inviteMobileController.logout();
                                        }}>
                                        {translate('INVITE.VIEW.LOGOUT')}
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                )}
                <div className='ts-main'>
                    <main>
                        <a className='anchor' id='main-top' />
                        <div className='invite-flex-wrapper mobile'>
                            <div className='ts-wizard-content ts-observer-invite mobile text-center'>
                                <div className='ts-invite-notifications' ref={this.notificationsRef}>
                                    {state.statusMessage && (
                                        <div className='text-center main-text-color ts-fade-out'>
                                            {translate(state.statusMessage)}
                                        </div>
                                    )}
                                    {state.message && (
                                        <div className='text-center main-text-color'>
                                            <h4>{translate(state.message)}</h4>
                                        </div>
                                    )}
                                    {state.customError && (
                                        <div className='ts-invite-message'>
                                            <img src={requireImage('attention.png')} alt='Attention' />
                                            <div className='ts-error-message'>{state.customError.message}</div>
                                        </div>
                                    )}
                                </div>
                                {allowedToJoinByCodeOrGuid && (
                                    <div className='text-center'>
                                        <b>{translate('INVITE.VIEW.ENTER_SESSION_ID')}</b>
                                        <div />
                                        <small className='orange-text'>
                                            {translate('INVITE.VIEW.CONNECT_CLIENT_SESSION_ID')}
                                        </small>
                                    </div>
                                )}
                                {allowedToJoinByCodeOrGuid && (
                                    <div className='invite-supervisor-forms row'>
                                        <form
                                            className='ts-inline-form col-sm-5'
                                            noValidate
                                            onSubmit={(e) => {
                                                e.preventDefault();
                                                if (state.meetingCode) {
                                                    inviteMobileController.joinByCode(state.meetingCode);
                                                }
                                            }}>
                                            <input
                                                type='text'
                                                name='meeting-code'
                                                id='meeting-code'
                                                className='form-control'
                                                placeholder={translate('INVITE.VIEW.TYPE_MEETING_ID')}
                                                value={state.meetingCode ?? ''}
                                                onChange={(event) =>
                                                    inviteMobileController.setMeetingCode(event.target.value)
                                                }
                                            />
                                            <button type='submit' className='btn btn-wizard'>
                                                {translate('INVITE.VIEW.JOIN')}
                                            </button>
                                        </form>
                                    </div>
                                )}

                                {showSuperviseSession && (
                                    <>
                                        <div className='ts-title-wrapper observe'>
                                            <div className='ts-observe-session-title text-center'>
                                                <b>{translate('INVITE.VIEW.SUPERVISE_SESSION')}</b>
                                                <div />
                                            </div>
                                        </div>
                                        <div className='invite-supervisor-forms row'>
                                            <form
                                                className='ts-observe-form ts-inline-form col-sm-5'
                                                noValidate
                                                onSubmit={async (e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    await inviteMobileController.superviseSession();
                                                    return false;
                                                }}>
                                                {inviteMobileController.observableUsersController ? (
                                                    <div className={isRTL ? 'ts-multi-select-rtl' : ''}>
                                                        <TsMultiSelect
                                                            controller={
                                                                inviteMobileController.observableUsersController
                                                            }
                                                        />
                                                    </div>
                                                ) : (
                                                    <select
                                                        name='observer-room'
                                                        className='form-control'
                                                        disabled
                                                        required>
                                                        <option value='' disabled>
                                                            {translate(
                                                                'REACT.INVITE.VIEW.SESSION_OBSERVATION.NO_SESSIONS_TO_OBSERVE'
                                                            )}
                                                        </option>
                                                    </select>
                                                )}
                                                <button
                                                    type='submit'
                                                    className='btn btn-wizard'
                                                    disabled={!state?.observableUsers?.length}>
                                                    {translate('INVITE.VIEW.JOIN')}
                                                </button>
                                            </form>
                                        </div>
                                    </>
                                )}
                                {allowedToJoinByCodeOrGuid && (
                                    <h4 className='invite-or-separator text-center'>
                                        <hr className='is-mobile' />
                                        <span>{translate('INVITE.VIEW.OR')}</span>
                                    </h4>
                                )}
                                <div className='ts-title-wrapper'>
                                    <h2 className='session-title'>{translate('INVITE.VIEW.START_NEW_SESSION')}</h2>
                                </div>
                                <div className='ts-invite text-center'>
                                    <div className='invite-mobile-message'>
                                        <div
                                            className={`invite-mobile-caption mobile-sub-title ${isRTL ? 'text-right' : 'text-left'}`}>
                                            {translate('INVITE.VIEW.CHOOSE_INVITE_METHOD')}
                                        </div>
                                        <div className={`invite-mobile-options text-center`}>
                                            {fsInviteBySMS && (
                                                <button
                                                    type='button'
                                                    className={`invite-mobile-option ${
                                                        state.inviteMethod === 'SMS' ? 'selected' : ''
                                                    }`}
                                                    onClick={() => {
                                                        inviteMobileController.setInviteMethod('SMS');
                                                        inviteMobileController.setShowMobileTechseeInvite(true);
                                                    }}>
                                                    <img
                                                        src={getInviteIcon('sms', state.inviteMethod === 'SMS')}
                                                        alt='send SMS'
                                                    />{' '}
                                                    SMS
                                                </button>
                                            )}
                                            {fsInviteByEmail && (
                                                <button
                                                    type='button'
                                                    className={`invite-mobile-option ${
                                                        state.inviteMethod === 'EMAIL' ? 'selected' : ''
                                                    }`}
                                                    onClick={() => {
                                                        inviteMobileController.setInviteMethod('EMAIL');
                                                        inviteMobileController.setShowMobileTechseeInvite(false);
                                                        // If customer ID is already filled, trigger the invite immediately
                                                        // Otherwise, just show the customer ID input (form is already hidden)
                                                        if (state.customerId) {
                                                            inviteMobileController.launchInviteMobile('email');
                                                        }
                                                    }}>
                                                    <img
                                                        src={getInviteIcon('email', state.inviteMethod === 'EMAIL')}
                                                        alt='send email'
                                                    />{' '}
                                                    Email
                                                </button>
                                            )}
                                            {fsInviteByWhatsApp && (
                                                <button
                                                    type='button'
                                                    className={`invite-mobile-option ${
                                                        state.inviteMethod === 'WHATSAPP' ? 'selected' : ''
                                                    }`}
                                                    onClick={() => {
                                                        inviteMobileController.setInviteMethod('WHATSAPP');
                                                        inviteMobileController.setShowMobileTechseeInvite(false);
                                                        // If customer ID is already filled, trigger the invite immediately
                                                        // Otherwise, just show the customer ID input (form is already hidden)
                                                        if (state.customerId) {
                                                            inviteMobileController.launchInviteMobile('whatsapp');
                                                        }
                                                    }}>
                                                    <img
                                                        src={getInviteIcon(
                                                            'whatsapp',
                                                            state.inviteMethod === 'WHATSAPP'
                                                        )}
                                                        alt='send whatsapp'
                                                    />{' '}
                                                    Whatsapp
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    {!state.showMobileTechseeInvite && customerIdSettings?.display && (
                                        <form className='invite-mobile-id-form'>
                                            <div className='text-center form-group'>
                                                <input
                                                    type='text'
                                                    name='alt-invite-id'
                                                    id='alt-invite-id'
                                                    placeholder={translate('INVITE.VIEW.TYPE_REFERENCE')}
                                                    className='form-control'
                                                    value={state.customerId ?? ''}
                                                    onChange={(event) =>
                                                        inviteMobileController.setCustomerId(event.target.value)
                                                    }
                                                />
                                            </div>
                                        </form>
                                    )}
                                    {state.showMobileTechseeInvite && (
                                        <div className='form-wrapper mobile'>
                                            <form
                                                className='ts-inline-form ts-phone-form'
                                                noValidate
                                                onSubmit={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                }}>
                                                <div className={`form-group ${isRTL ? 'text-right' : 'text-left'}`}>
                                                    <label className='mobile-sub-title' htmlFor='session-type'>
                                                        {translate('INVITE.VIEW.SESSION_TYPE')}
                                                    </label>
                                                    {inviteMobileController.sessionTypeController && (
                                                        <div className={isRTL ? 'ts-multi-select-rtl' : ''}>
                                                            <TsMultiSelect
                                                                controller={
                                                                    inviteMobileController.sessionTypeController
                                                                }
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                                {showLanguageSelection && (
                                                    <div className={`form-group ${isRTL ? 'text-right' : 'text-left'}`}>
                                                        <label className='mobile-sub-title' htmlFor='language-mobile'>
                                                            {translate('INVITE.VIEW.LANGUAGE')}
                                                        </label>
                                                        {inviteMobileController.languageController && (
                                                            <div className={isRTL ? 'ts-multi-select-rtl' : ''}>
                                                                <TsMultiSelect
                                                                    controller={
                                                                        inviteMobileController.languageController
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                {showAudioToggle && (
                                                    <div
                                                        className='offline-radio-buttons-section mobile-sub-title'
                                                        style={{justifyContent: 'space-between'}}>
                                                        <span className='radio-group-header'>
                                                            {translate('INVITE.VIEW.START_AUDIO_DURING_SESSION')}
                                                        </span>
                                                        <SwitchButton
                                                            value={state.enableAudio ?? false}
                                                            onChange={() => {
                                                                const newValue = !state.enableAudio;
                                                                inviteMobileController.setEnableAudio(newValue);
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                                <label
                                                    className='mobile-sub-title'
                                                    style={{
                                                        display: 'block',
                                                        width: '100%',
                                                        textAlign: 'start',
                                                        fontSize: '16px',
                                                        fontWeight: 'bold',
                                                        marginBottom: '20px'
                                                    }}>
                                                    {translate('INVITE.VIEW.CUSTOMER_DETAILS')}
                                                </label>
                                                {customerIdSettings?.display && (
                                                    <div className='form-group'>
                                                        <input
                                                            type='text'
                                                            name='invite-id'
                                                            id='invite-id'
                                                            placeholder={translate('INVITE.VIEW.TYPE_REFERENCE')}
                                                            className='form-control is-mobile ts-customer-ref'
                                                            value={state.customerId ?? ''}
                                                            onChange={(event) =>
                                                                inviteMobileController.setCustomerId(event.target.value)
                                                            }
                                                        />
                                                    </div>
                                                )}
                                                {((allowInviteBySms && !accountOfflineSessions) ||
                                                    (accountOfflineSessions && state.inviteMethod === 'SMS')) && (
                                                    <div
                                                        className={`form-group invite-phone-wrapper ${
                                                            addCountryCodeToSms ? 'country-div' : ''
                                                        }`}>
                                                        {inviteMobileController.countrySelectController && (
                                                            <TsCountrySelect
                                                                ctrl={inviteMobileController.countrySelectController}
                                                            />
                                                        )}
                                                        <input
                                                            type='tel'
                                                            inputMode='numeric'
                                                            name='invite-phone'
                                                            id='invite-phone'
                                                            placeholder={translate('INVITE.VIEW.SEND_SMS')}
                                                            className='form-control invite-phone'
                                                            value={state.phone ?? ''}
                                                            pattern={phonePattern}
                                                            onChange={(event) => {
                                                                const value = event.target.value;
                                                                // Allow only digits, spaces, dashes, parentheses, and plus sign
                                                                const filteredValue = value.replace(
                                                                    /[^\d\s\-\(\)\+]/g,
                                                                    ''
                                                                );
                                                                inviteMobileController.setPhone(filteredValue);
                                                            }}
                                                            required
                                                        />
                                                    </div>
                                                )}
                                                <div className='invite-buttons offline'>
                                                    <button
                                                        type='button'
                                                        className='btn btn-wizard main-send'
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            const inviteMethod = state.inviteMethod;
                                                            if (inviteMethod === 'SMS') {
                                                                inviteMobileController
                                                                    .launchInviteMobile('sms')
                                                                    .catch(() => {
                                                                        // Error handled in controller
                                                                    });
                                                            } else if (inviteMethod === 'EMAIL') {
                                                                inviteMobileController
                                                                    .launchInviteMobile('email')
                                                                    .catch(() => {
                                                                        // Error handled in controller
                                                                    });
                                                            } else if (inviteMethod === 'WHATSAPP') {
                                                                inviteMobileController
                                                                    .launchInviteMobile('whatsapp')
                                                                    .catch(() => {
                                                                        // Error handled in controller
                                                                    });
                                                            }
                                                        }}>
                                                        {state.inviteMethod === 'EMAIL'
                                                            ? translate('INVITE.VIEW.CLICK_SEND_EMAIL')
                                                            : state.inviteMethod === 'WHATSAPP'
                                                              ? translate('REACT.WHATSAPP.SEND_WHATSAPP')
                                                              : translate('INVITE.VIEW.CLICK_SEND_SMS')}
                                                    </button>
                                                </div>
                                            </form>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
                {inviteMobileController.changeSettingsModalController && (
                    <ChangeSettingsModal ctrl={inviteMobileController.changeSettingsModalController} />
                )}
                {inviteMobileController.changeAccountModalController && (
                    <ChangeAccountModal ctrl={inviteMobileController.changeAccountModalController} />
                )}
                {inviteMobileController.simpleModalController && (
                    <SimpleModal ctrl={inviteMobileController.simpleModalController} />
                )}
                {inviteMobileController.acceptTermsModalController && (
                    <AcceptTermsModal ctrl={inviteMobileController.acceptTermsModalController} />
                )}
                {inviteMobileController.smsStatusModalController && (
                    <SmsStatusModal ctrl={inviteMobileController.smsStatusModalController} />
                )}
                {inviteMobileController.lookupNumberController && (
                    <LookupNumber ctrl={inviteMobileController.lookupNumberController} />
                )}
            </div>
        );
    }
}

@inject((stores: any) => ({inviteMobileController: stores.inviteStore?.inviteMobileController}))
export class InviteMobileView extends React.Component<InviteMobileViewProps> {
    render(): JSX.Element {
        if (!this.props.inviteMobileController) {
            return <ScreenLoader />;
        }
        return <InviteMobileViewComponent inviteMobileController={this.props.inviteMobileController} />;
    }
}

export class InviteMobileRootComponent extends React.Component<InviteMobileRootProps> {
    render(): JSX.Element {
        const {stores} = this.props;

        if (typeof stores !== 'object' || !stores) {
            return <ScreenLoader />;
        }

        const providerStores: Record<string, unknown> = {};

        Object.getOwnPropertyNames(stores).forEach((storeName: string) => {
            providerStores[storeName] = (stores as Record<string, unknown>)[storeName];
        });

        return (
            <Provider {...providerStores}>
                <InviteMobileView />
            </Provider>
        );
    }
}
